import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import InputWithIcon from '../../components/generics/input-with-icon';
import AuthButton from '../../components/auth/auth-button';
import AlertMessage from '../../components/alert-message';
//import GoogleIcon from '../../components/icons/GoogleIcon';
//import OauthButton from '../../components/auth/oauth-button';
//import Separator from '../../components/auth/separator';
import NavigationLink from '../../components/generics/navigation-link';
import {useAuthViewModel} from "../../view-model/auth-model-view"
import {observer} from "mobx-react-lite";
import LanguageSwitcher from '../../components/generics/language-switcher';
import PasswordInputWithValidation from '../../components/generics/password-input-with-validation';
import ConfirmPasswordInput from '../../components/generics/confirm-password-input';
import { validateEmail } from '../../utils/validation';

const SignupPage: React.FC = observer(() => {
  const authViewModel = useAuthViewModel();
  const { t } = useTranslation();

  // États de validation
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isConfirmPasswordValid, setIsConfirmPasswordValid] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState<string | null>(null);
  
  // Vérifier la validité de l'email
  useEffect(() => {
    if (authViewModel.email) {
      const validation = validateEmail(authViewModel.email);
      setIsEmailValid(validation.isValid);
      setEmailErrorMessage(validation.isValid ? null : validation.message);
    } else {
      setIsEmailValid(false);
      setEmailErrorMessage(null);
    }
  }, [authViewModel.email]);
  
  // Vérifier si le formulaire est valide
  const isFormValid = () => {
    return (
      authViewModel.firstName.trim() !== '' &&
      authViewModel.lastName.trim() !== '' &&
      isEmailValid &&
      isPasswordValid &&
      isConfirmPasswordValid &&
      authViewModel.acceptTerms
    );
  };

  return (
    <div className="min-h-screen w-full bg-gray-50 flex items-center justify-center overflow-hidden py-4 md:py-8 px-4 relative">
      {/* Représentation subtile de calendrier/planning en arrière-plan */}
      <div className="absolute inset-0 opacity-[0.15] pointer-events-none">
        {/* Grille de planning stylisée */}
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(to right, rgb(35, 168, 146) 1px, transparent 1px),
            linear-gradient(to bottom, rgb(35, 168, 146) 1px, transparent 1px)
          `,
          backgroundSize: '80px 60px',
          transform: 'perspective(800px) rotateX(45deg) scale(1.5)',
          transformOrigin: 'center center',
        }}></div>
      </div>

      <div className="w-full max-w-xl bg-white/95 backdrop-blur-md p-4 md:p-8 shadow-2xl rounded-2xl border border-gray-100 relative z-10">
        {/* Formulaire sans défilement interne */}
        <div className="w-full p-4 md:p-8">
          <div className="mb-6 flex items-center justify-between">
            <a href={`${process.env.REACT_APP_HOME_URL}`} className="flex items-center">
              <img src="/prestaslot_logo.svg" alt="PrestaSlot" className="h-7 sm:h-8 md:h-10" />
            </a>
            <LanguageSwitcher />
          </div>
          
          <div className="mb-6 text-center">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">{t('common.signup')}</h1>
            <p className="text-base md:text-lg text-gray-600">{t('auth.signupTitle')}</p>
          </div>
          {authViewModel.message && (
              <AlertMessage message={authViewModel.message} type="success" />
          )}

          {authViewModel.message && (
              <AlertMessage message={authViewModel.message} type="success" />
          )}

          {authViewModel.errorMessage && (
              <AlertMessage message={authViewModel.errorMessage} type="error" />
          )}

          <form onSubmit={(e) => authViewModel.register(e)} className="space-y-5">
            <div className="flex items-center justify-between w-full gap-4">
              <div className="w-full">
                <InputWithIcon
                  label={t('common.firstName')}
                  type="firstName"
                  id="firstName"
                  value={authViewModel.firstName}
                  onChange={e => authViewModel.setFirstName(e.target.value)}
                  required
                  placeholder="Ex: john"
                  disableDarkMode={true}
                />
              </div>
              <div className="w-full">
                <InputWithIcon
                  label={t('common.lastName')}
                  type="lastName"
                  id="lastName"
                  value={authViewModel.lastName}
                  onChange={e => authViewModel.setLastName(e.target.value)}
                  required
                  placeholder="ex: doe"
                  disableDarkMode={true}
                />
              </div>
            </div>
            <InputWithIcon
                label={t('common.email')}
                type="email"
                id="email"
                value={authViewModel.email}
                onChange={e => authViewModel.setEmail(e.target.value)}
                required
                placeholder="votre@email.com"
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none"
                       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                }
                disableDarkMode={true}
            />
            {emailErrorMessage && (
              <p className="text-red-500 text-xs mt-1">{emailErrorMessage}</p>
            )}

            <PasswordInputWithValidation
                label={t('common.password')}
                id="password"
                name={t('common.password')}
                value={authViewModel.password}
                onChange={e => authViewModel.setPassword(e.target.value)}
                required
                placeholder={t('common.createPassword') || 'Créez un mot de passe'}
                autoComplete="new-password"
                onValidationChange={setIsPasswordValid}
                forceLightMode={true}
            />

            <ConfirmPasswordInput
                label={t('common.confirmPassword')}
                id="confirmPassword"
                name={t('common.confirmPassword')}
                value={authViewModel.confirmPassword}
                password={authViewModel.password}
                onChange={e => authViewModel.setConfirmPassword(e.target.value)}
                required
                placeholder={t('common.confirmPassword') || 'Confirmez votre mot de passe'}
                autoComplete="new-password"
                onValidationChange={setIsConfirmPasswordValid}
                forceLightMode={true}
            />

            <div className="flex items-start mt-2">
              <div className="flex items-center h-5">
                <input
                    type="checkbox"
                    id="terms"
                    checked={authViewModel.acceptTerms}
                    onChange={(e) => authViewModel.setAcceptTerms(e.target.checked)}
                    required
                    className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="terms" className="text-gray-600">
                  {t('common.termsAccept')} <NavigationLink to="/terms"
                                                            className="text-primary hover:text-primary-dark transition-colors duration-300">{t('common.termsLink')}</NavigationLink>
                </label>
                <label htmlFor="privacy" className="text-gray-600">
                  {t('common.privacyAccept')} <NavigationLink to="/privacy"
                                                            className="text-primary hover:text-primary-dark transition-colors duration-300">{t('common.privacyLink')}</NavigationLink>
                </label>
              </div>
            </div>

            <div className="mt-6">
              <AuthButton 
                type="submit" 
                disabled={!isFormValid()}
                className={!isFormValid() ? 'opacity-50 cursor-not-allowed' : ''}
              >
                {t('common.signupAction')}
              </AuthButton>
            </div>

            {/* <Separator className="my-6"/>

            <OauthButton
                provider="google"
                onClick={() => authViewModel.loginWithGoogle()}
                icon={<GoogleIcon className="w-5 h-5"/>}
                text={t('auth.signInWithGoogle') || 'Continuer avec Google'}
            /> */}

            <div className="mt-6 text-center text-sm text-gray-600">
              <p>{t('common.alreadyHaveAccount') || 'Vous avez déjà un compte ?'} <NavigationLink to="/login"
                                                                                                  className="text-primary font-medium hover:text-primary-dark transition-colors duration-300">{t('common.login') || 'Se connecter'}</NavigationLink>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
});

export default SignupPage;
